'use es6';

import Immutable from 'immutable';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { BULK_MOVE_ATTEMPTED, BULK_MOVE_SUCCEEDED, BULK_MOVE_FAILED, BULK_DELETE_ATTEMPTED, BULK_DELETE_SUCCEEDED, BULK_DELETE_FAILED } from 'FileManagerCore/actions/ActionTypes';
import { COUNT_FILES_ATTEMPTED, COUNT_FILES_SUCCEEDED, COUNT_FILES_FAILED, START_START_BULK_VISIBILITY_CHANGE_ATTEMPTED, START_BULK_VISIBILITY_CHANGE_SUCCESS, START_BULK_VISIBILITY_CHANGE_FAILURE, ADD_BULK_FILE_SELECTION, ADD_BULK_FOLDER_SELECTION, REMOVE_BULK_FILE_SELECTION, REMOVE_BULK_FOLDER_SELECTION, CLEAR_BULK_SELECTION } from './ActionTypes';
import { isFile } from 'FileManagerCore/utils/FoldersAndFiles';
import { getDecreaseVideoQuantityUsedAction } from 'FileManagerCore/actions/Limits';
import { getTrackingMeta } from 'FileManagerCore/actions/tracking';
import { isHubLVideo } from 'FileManagerCore/utils/hubLVideo';
import { reportError } from 'FileManagerCore/utils/logging';
import { exportFiles } from './Files';
import { deleteAll, move, changeVisibility, getFileCountFromPublicSearch } from '../api/Bulk';
import { getBulkChangeVisibilitySuccessNotification, getBulkChangeVisibilityFailureNotification, getNestedFileCountFailureNotification } from '../utils/notifications';
import { jsx as _jsx } from "react/jsx-runtime";
function getFilesAndFolders(bulkSelection) {
  let files = [];
  let folders = [];
  bulkSelection.forEach(object => {
    if (isFile(object)) {
      files.push(object);
    } else {
      folders.push(object);
    }
  });
  files = new Immutable.List(files);
  folders = new Immutable.List(folders);
  return {
    files,
    folders
  };
}
export function addFileBulkSelection(bulkFileIds) {
  return {
    type: ADD_BULK_FILE_SELECTION,
    bulkFileIds: Immutable.Set(bulkFileIds)
  };
}
export function addFolderBulkSelection(bulkFolderIds) {
  return {
    type: ADD_BULK_FOLDER_SELECTION,
    bulkFolderIds: Immutable.Set(bulkFolderIds)
  };
}
export function removeFileBulkSelection(bulkFileIds) {
  return {
    type: REMOVE_BULK_FILE_SELECTION,
    bulkFileIds: Immutable.Set(bulkFileIds)
  };
}
export function removeFolderBulkSelection(bulkFolderIds) {
  return {
    type: REMOVE_BULK_FOLDER_SELECTION,
    bulkFolderIds: Immutable.Set(bulkFolderIds)
  };
}
export function clearBulkSelection() {
  return {
    type: CLEAR_BULK_SELECTION
  };
}
export function bulkDelete(bulkSelection) {
  return dispatch => {
    const {
      files,
      folders
    } = getFilesAndFolders(bulkSelection);
    dispatch({
      type: BULK_DELETE_ATTEMPTED,
      meta: getTrackingMeta('fileManagerManageFiles', 'bulk delete'),
      files,
      folders
    });
    deleteAll(files.map(file => file.get('id')).toArray(), folders.map(folder => folder.get('id')).toArray()).then(() => {
      dispatch({
        type: BULK_DELETE_SUCCEEDED,
        files,
        folders
      });
      const hublVideoFiles = files.filter(file => isHubLVideo(file.toJS()));
      if (hublVideoFiles.size) {
        dispatch(getDecreaseVideoQuantityUsedAction(hublVideoFiles.size));
      }
    }).catch(error => {
      reportError(error, {
        type: BULK_DELETE_FAILED
      });
      const prefix = 'FileManagerDashboard.notifications.bulkTrash.error';
      dispatch({
        type: BULK_DELETE_FAILED,
        meta: {
          notification: {
            type: 'danger',
            titleText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `${prefix}.title`
            }),
            message: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `${prefix}.message`,
              options: {
                count: bulkSelection.size
              }
            })
          }
        },
        error,
        files,
        folders
      });
    });
  };
}
export function bulkMove(bulkSelection, destination) {
  return dispatch => {
    const {
      files,
      folders
    } = getFilesAndFolders(bulkSelection);
    dispatch({
      type: BULK_MOVE_ATTEMPTED,
      meta: getTrackingMeta('fileManagerManageFiles', 'bulk move'),
      files,
      folders,
      destination
    });
    move(files.map(file => file.get('id')).toArray(), folders.map(folder => folder.get('id')).toArray(), destination.get('full_path')).then(response => {
      dispatch({
        type: BULK_MOVE_SUCCEEDED,
        files,
        folders,
        updatedFiles: response.get('files'),
        updatedFolders: response.get('folders'),
        destination
      });
    }).catch(error => {
      reportError(error, {
        type: BULK_MOVE_FAILED
      });
      dispatch({
        type: BULK_MOVE_FAILED,
        meta: {
          notification: {
            type: 'danger',
            titleText: I18n.text('FileManagerDashboard.notifications.bulkMove.error.title'),
            message: I18n.text('FileManagerDashboard.notifications.bulkMove.error.message', {
              count: bulkSelection.size
            })
          }
        },
        error,
        files,
        folders
      });
    });
  };
}
function getExportSelectedFilesAndFoldersRequestPayload(selectedFilesAndFolders) {
  return {
    folder_paths: selectedFilesAndFolders.toList().filter(fileOrFolder => !isFile(fileOrFolder)).map(folder => folder.get('full_path')).toJS(),
    file_ids: selectedFilesAndFolders.toList().filter(isFile).map(file => file.get('id')).toJS()
  };
}
export function exportSelectedFilesAndFolders(emails, selectedFilesAndFolders) {
  return exportFiles(emails, getExportSelectedFilesAndFoldersRequestPayload(selectedFilesAndFolders));
}
export function bulkChangeVisibility(visibility, selection, fileCount, onSuccessCallback) {
  const {
    files,
    folders
  } = getFilesAndFolders(selection);
  const fileIds = files.map(file => file.get('id')).toArray();
  const folderIds = folders.map(folder => folder.get('id')).toArray();
  return dispatch => {
    dispatch({
      type: START_START_BULK_VISIBILITY_CHANGE_ATTEMPTED
    });
    changeVisibility({
      visibility,
      fileIds,
      folderIds
    }).then(() => {
      dispatch({
        type: START_BULK_VISIBILITY_CHANGE_SUCCESS,
        meta: {
          notification: getBulkChangeVisibilitySuccessNotification(fileCount)
        }
      });
      onSuccessCallback();
    }).catch(error => {
      reportError(error, {
        type: START_BULK_VISIBILITY_CHANGE_FAILURE
      });
      dispatch({
        type: START_BULK_VISIBILITY_CHANGE_FAILURE,
        meta: {
          notification: getBulkChangeVisibilityFailureNotification()
        }
      });
    });
  };
}
export const getCountNestedFileCountAttemptedAction = () => {
  return {
    type: COUNT_FILES_ATTEMPTED
  };
};
export const getCountNestedFileCountSucceededAction = count => {
  return {
    type: COUNT_FILES_SUCCEEDED,
    totalNestedFileCount: count
  };
};
export const getCountNestedFileCountFailedAction = (showFailureNotification = true) => {
  return {
    type: COUNT_FILES_FAILED,
    meta: {
      notification: showFailureNotification && getNestedFileCountFailureNotification()
    }
  };
};
export function getNestedFolderFileCount(folderIdList, showFailureNotification = true) {
  if (folderIdList.size === 0) {
    return dispatch => dispatch(getCountNestedFileCountSucceededAction(0));
  }
  return dispatch => {
    dispatch(getCountNestedFileCountAttemptedAction());
    getFileCountFromPublicSearch(folderIdList).then(count => {
      if (isNaN(parseInt(count, 10))) {
        dispatch(getCountNestedFileCountFailedAction(showFailureNotification));
      } else {
        dispatch(getCountNestedFileCountSucceededAction(parseInt(count, 10)));
      }
    }).catch(err => {
      dispatch(getCountNestedFileCountFailedAction(showFailureNotification));
      reportError(err, {
        type: COUNT_FILES_FAILED
      });
    });
  };
}